/**
 * The shapes the media feature is built from: the media enums, the library
 * UI, clips and the canvas objects behind them, and media-name lookups.
 *
 * `MediaElementReadyState` mirrors the browser's own HTMLMediaElement
 * readyState values, `MediaOrigin` says where a media file came from, and
 * `MediaType` says what kind of media it is.
 */
import type { CancelToken } from 'axios';
import type { FabricImage, FabricObject, Group } from 'fabric';
import type { useEditorStore } from '@/stores/editorStore';
import type { MediaKind } from '@/types/library/gallery';
import type { Layer } from '@/types/layers/layer';

export enum MediaElementReadyState {
  HAVE_NOTHING = 0,
  HAVE_METADATA = 1,
  HAVE_CURRENT_DATA = 2,
  HAVE_FUTURE_DATA = 3,
  HAVE_ENOUGH_DATA = 4
}

export enum MediaOrigin {
  None,
  UserUpload,
  Library
}

export enum MediaType {
  Frame = 1,
  SaleBadge = 2,
  Offers = 3,
  Banner = 4,
  Image = 5,
  ThreeD = 6,
  Video = 7,
  Audio = 8,
  /** Canvas-only uploads (e.g. 3D rotation snapshots); hidden from gallery panels. Must match backend RoimaticImageType.Temp. */
  Temp = 9,
  /** Photos listed in the Background panel's rows. Must match backend RoimaticImageType. */
  Landscape = 10,
  Architecture = 11
}

/*
 * Library UI
 */

/** The tabs in the Upload panel. */
export type MediaTabKind = 'images' | 'videos' | 'audio';

export interface MediaTab {
  kind: MediaTabKind;
  /** i18n key for the tab label. */
  label: string;
  /** Material icon used in the empty state. */
  icon: string;
  /** i18n key for the empty-state message. */
  emptyMessage: string;
  /** Whether this kind is actually wired up to a backend yet. */
  ready: boolean;
}

/** How media is fitted inside its layer. */
export type FitOptions = 1 | 2 | 3 | 4;

/**
 * Where a media element is playing. Only one file plays at a time within a
 * scope, but different scopes do not interfere with each other.
 */
export type MediaPlaybackScope = 'canvas' | 'gallery' | 'details';

/*
 * Clips and canvas objects
 */

/**
 * Options for placing a layer on the clock: which ad's values to read, the
 * file's real length if known, and how long an untimed layer should run for.
 */
export interface MakeClipOptions {
  adId?: string | null;
  loadedDuration?: number | null;
  untimedWindowEnd?: number;
  resolve?: (layerId: string) => Layer | null;
}

/**
 * What a media element was doing after it was positioned: finished, off
 * screen, or playing.
 */
export type ClipElementState = 'ended' | 'outside' | 'playing';

/** Whether parking clips should also show or hide them. */
export interface SeekVideoClipsOptions {
  applyVisibility?: boolean;
  strict?: boolean;
  signal?: AbortSignal;
}

/** Anything holding canvas objects — a canvas or a group. */
export interface FabricObjectContainer {
  getObjects: () => FabricObject[];
}

/** A video found on a canvas: its element, its picture, and its group. */
export interface CanvasVideo {
  element: HTMLVideoElement;
  image: FabricImage;
  group: Group;
}

/*
 * Services and library operations
 */

export type EditorStore = ReturnType<typeof useEditorStore>;

/** One element the playback manager is currently tracking. */
export interface ActiveMediaItem {
  element: HTMLAudioElement | HTMLVideoElement;
  groupId?: string;
  scope: MediaPlaybackScope;
  onPauseCallback?: () => void;
  standalone?: boolean;
}

/** The i18n translate function, passed in so services stay UI-free. */
export type TranslateFn = (
  key: string,
  named?: Record<string, unknown>
) => string;

/** Options for a library load: start a fresh page, and which media type. */
export interface LoadMediaOptions {
  reset?: boolean;
  mediaType?: MediaType;
  imageType?: MediaType;
}

/** One name to look up, and which kind of media it should be. */
export interface MediaNameRequest {
  value: string;
  mediaKind: MediaKind;
}

/**
 * The answer to a batch of name lookups: how to read a resolved URL, plus
 * counts of what matched, what matched nothing, and what never got an answer.
 */
export interface MediaNameResolution {
  lookup: (value: string, mediaKind: MediaKind) => string | undefined;
  totalCount: number;
  resolvedCount: number;
  missCount: number;
  failedCount: number;
}

/** What one library's lookup pass found, and which names failed outright. */
export interface LookupPassResult {
  unresolved: MediaNameRequest[];
  failedKeys: Set<string>;
}

/**
 * Per-request hooks for an upload: how to cancel it, and a progress callback
 * so the caller can follow bytes leaving the browser. All three are passed
 * straight through to axios.
 *
 * Cancellation is given twice on purpose. `@deltax/api-client` pins its own
 * axios 0.21.4, whose XHR adapter predates `AbortSignal` and honours only
 * `cancelToken` — pass just a signal and cancelling silently does nothing.
 * The signal is here so cancelling keeps working if that dependency is ever
 * upgraded to an axios that ignores the deprecated token.
 */
export interface MediaUploadOptions {
  signal?: AbortSignal;
  cancelToken?: CancelToken;
  onUploadProgress?: (event: { loaded: number; total?: number }) => void;
}

/**
 * How a seek on a video element ended: `ready` once the requested position
 * has decoded data, `superseded` when a later seek took over, `timeout` when
 * the element never got there, and `failed` for an unusable request.
 */
export type VideoSeekOutcome = 'ready' | 'timeout' | 'failed' | 'superseded';

export interface VideoSeekOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}
